import { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Roles, routes, subTabs, tabs } from 'src/enums';
import { RootState } from 'src/redux/store';
import { TUserSlice } from 'src/redux/userSlice';
import { TopTabsAdmin } from './tabs/TopTabsAdmin';
import { ClientsList } from '../clients/ClientsList';
import { EventLog } from '../eventLog/EventLogList';
import { ClientDetails } from '../clientDetails/ClientDetails';
import { SettingsWrapper } from '../settings/SettingsWrapper';
import { ApplicationUserInfo } from '../applications/old/ApplicationUserInfo';
import { DeleteProfileAsAdmin } from '../profile/DeleteProfileAsAdmin';
import { Route } from 'react-router';
import LinearProgress from '@mui/material/LinearProgress';
import { CreateApplication } from '../applications/old/CreateApplication';

interface IAdminRouteProps {
  profile: TUserSlice['profile'];
  isAuthorized: TUserSlice['isAuthorized'];
  roles: TUserSlice['roles'];
}

const mapStateToProps = ({ user }: RootState) => ({
  profile: user.profile,
  isAuthorized: user.isAuthorized,
  roles: user.roles,
});

const AdminRouteComponent: FC<IAdminRouteProps> = ({ profile, isAuthorized, roles }) => {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const location = useLocation();

  const userId = profile?.id;
  const passwordChangeRequired = profile?.password_change_required;

  const needsProfileFill =
    location.pathname !== '/fill-profile' && userId && passwordChangeRequired;

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthorized) return;
      if (!profile.id) return;

      if (roles) {
        const isAdminMiniApp = roles.some((item) => item.role === Roles.EDITOR);
        setHasAccess(isAdminMiniApp);
      }
    };

    checkAuth();
  }, [isAuthorized, profile, roles]);

  if (hasAccess === null) return <LinearProgress />;
  if (!isAuthorized) return <Navigate to="/login" replace />;
  if (!hasAccess) return <Navigate to={`/${routes.profile}`} replace />;
  if (needsProfileFill) return <Navigate to="/fill-profile" replace />;

  return (
    <>
      <TopTabsAdmin />
      <Outlet />
    </>
  );
};

export const AdminRoute = connect(mapStateToProps)(AdminRouteComponent);

export const getAdminRoutes = (orgId: TUserSlice['orgId']) => [
  <Route
    key="index"
    index
    element={<Navigate to={`/${routes.admin}/${orgId}/${tabs.clients}`} replace />}
  />,

  <Route
    key="event-log"
    path={`/${routes.admin}/:appId/${tabs.eventLog}`}
    element={<EventLog />}
  />,

  <Route
    key="clients"
    path={`/${routes.admin}/:appId/${tabs.clients}`}
    element={<ClientsList />}
  />,
  <Route
    key="create-client"
    path={`/${routes.admin}/:appId/${tabs.clients}/${subTabs.create}`}
    element={<CreateApplication />}
  />,
  <Route
    key="client-details"
    path={`/${routes.admin}/:appId/${tabs.clients}/:clientId`}
    element={<ClientDetails />}
  />,
  <Route
    key="client-settings"
    path={`/${routes.admin}/:appId/${tabs.clients}/:clientId/${tabs.settings}`}
    element={<SettingsWrapper />}
  />,
  <Route
    key="client-user-info"
    path={`/${routes.admin}/:appId/${tabs.clients}/:clientId/${tabs.users}/:userId`}
    element={<ApplicationUserInfo />}
  />,
  <Route
    key="client-user-delete"
    path={`/${routes.admin}/:appId/${tabs.clients}/:clientId/${tabs.users}/:userId/${subTabs.delete}`}
    element={<DeleteProfileAsAdmin />}
  />,
];
